import React, { useState } from 'react';

import pinIcon from "../../../styles/icons/pincode.png";
import supportIcon from "../../../styles/icons/customer-support.png";
import logoutIcon from "../../../styles/icons/logout.png";
import langIcon from "../../../styles/icons/language-empty.svg"

import { useTranslation } from "react-i18next";

const ProfileMenu = ({ onMenuItemClick, hasPinCode = false }) => {
  const {t, i18n} = useTranslation("profile")
  
  const [expandedMenu, setExpandedMenu] = useState(null);

  const getMyLanguageMessage = (msg) => {
    if(msg){
      const code = msg.substring(0, 2).toLowerCase();
      if (code === i18n.language){
        return "YOUR_LANGUAGE"
      }
      else{
        return msg
      }
    }
  }

  const menuItems = [
    {
      id: 'pin-menu',
      icon: pinIcon,
      title: 'PIN_MENU_TITLE',
      description: hasPinCode ? 'PIN_MENU_DESC_HAVE_PIN' : 'PIN_MENU_DESC_NO_PIN',
      expandable: true,
      subItems: hasPinCode ? [
        {
          id: 'change-pin',
          title: 'CHANGE_PIN_TITLE',
          description: 'CHANGE_PIN_DESC'
        },
        {
          id: 'remove-pin',
          title: 'REMOVE_PIN_TITLE',
          description: 'REMOVE_PIN_DESC',
          danger: true
        }
      ] : [
        {
          id: 'create-pin',
          title: 'CREATE_PIN_TITLE',
          description: 'CREATE_PIN_DESC'
        }
      ]
    },
    { 
      id: 'support',
      icon: supportIcon, 
      title: 'SUPPORT_TITLE',
      description: 'SUPPORT_DESC'
    },
    {
      id: 'language',
      icon: langIcon, 
      title: 'LANGUAGE_TITLE',
      description: 'LANGUAGE_DESC',
      expandable: true,
      subItems: [
        {
          id: 'ru-lang',
          title: 'RU_LANG_TITLE',
          description: getMyLanguageMessage('RU_LANG_DESC'),
        },
        {
          id: 'uz-lang',
          title: 'UZ_LANG_TITLE',
          description: getMyLanguageMessage('UZ_LANG_DESC'),
        },
        {
          id: 'en-lang',
          title: 'EN_LANG_TITLE',
          description: getMyLanguageMessage('EN_LANG_DESC'),
        },
      ]
    },
    { 
      id: 'logout',
      icon: logoutIcon, 
      title: 'LOGOUT_TITLE',
      description: 'LOGOUT_DESC',
      danger: true
    }
  ];

  const handleMenuClick = (item) => {
    if (item.expandable) {
      setExpandedMenu(expandedMenu === item.id ? null : item.id);
      return;
    }

    if (onMenuItemClick) {
      onMenuItemClick(item);
    } else {
      console.log('Действие:', item.id);
    }
  };

  const getIconFilter = (item, isSubItem = false) => {
    if (item.danger) {
      return 'invert(26%) sepia(89%) saturate(1583%) hue-rotate(331deg) brightness(71%) contrast(97%)';
    }
    if (isSubItem) {
      return 'invert(45%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)';
    }
    return 'invert(60%) sepia(0%) saturate(0%) hue-rotate(0deg) brightness(0%) contrast(100%)';
  };

  const renderMenuItem = (item, isSubItem = false) => (
    <button 
      key={item.id} 
      className={`
        w-full flex items-center gap-4 bg-transparent border-none cursor-pointer 
        transition-all duration-300 text-left
        ${isSubItem 
          ? 'p-4 pl-12 bg-gray-50 border-l-4 border-blue-200' 
          : 'p-5 border-b border-gray-100 hover:bg-gray-50'
        }
        ${item.danger 
          ? 'text-red-500 hover:bg-red-50' 
          : isSubItem 
            ? 'hover:bg-gray-100' 
            : ''
        }
      `}
      onClick={() => handleMenuClick(item)}
    >
      {/* рендерим иконку только если есть */}
      {item.icon && (
        <div className={`flex items-center justify-center ${isSubItem ? 'w-5 h-5' : 'w-6 h-6'}`}>
          <img 
            src={item.icon} 
            alt={t(item.title)}
            className={`${isSubItem ? 'w-4 h-4' : 'w-5 h-5'} transition-all duration-200`}
            style={{ filter: getIconFilter(item, isSubItem) }}
          />
        </div>
      )}
      
      <div className="flex-1 flex flex-col gap-1">
        <span className={`
          font-medium
          ${item.danger ? 'text-red-500' : 'text-gray-800'}
          ${isSubItem ? 'text-sm' : 'text-base'}
        `}>
          {t(item.title)}
        </span>
        {item.description && (
          <span className={`text-gray-500 leading-tight ${isSubItem ? 'text-xs' : 'text-sm'}`}>
            {t(item.description)}
          </span>
        )}
      </div>
      
      {item.expandable && (
        <span className={`
          text-lg transition-transform duration-200
          ${expandedMenu === item.id ? 'rotate-90' : ''}
          ${item.danger ? 'text-red-300' : 'text-gray-400'}
        `}>
          →
        </span>
      )}
      {!item.expandable && !isSubItem && (
        <span className={`
          text-lg transition-transform duration-200
          ${item.danger ? 'text-red-300' : 'text-gray-300'}
        `}>
          →
        </span>
      )}
    </button>
  );

  return (
    <div className="bg-white rounded-2xl overflow-hidden shadow-lg animate-fadeIn">
      {menuItems.map((item) => (
        <div key={item.id}>
          {renderMenuItem(item)}
          
          {item.expandable && expandedMenu === item.id && (
            <div className="animate-slideDown">
              {item.subItems.map(subItem => renderMenuItem(subItem, true))}
            </div>
          )}
        </div>
      ))}
      
      <style jsx>{`
        @keyframes fadeIn {
          from {
            opacity: 0;
            transform: translateY(20px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        
        @keyframes slideDown {
          from {
            opacity: 0;
            max-height: 0;
            transform: translateY(-10px);
          }
          to {
            opacity: 1;
            max-height: 200px;
            transform: translateY(0);
          }
        }
        
        .animate-fadeIn {
          animation: fadeIn 0.4s ease-out;
        }
        
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default ProfileMenu;
